import { useMemo, useState } from "react";
import {
  Button,
  Dialog,
  Heading,
  Modal,
  ModalOverlay,
  TextArea,
} from "react-aria-components";
import { Check, Mail, ShieldCheck, X } from "lucide-react";
import { getCsrf } from "../api";

type Provider = "google_oauth" | "google_workspace_dwd" | "lark_individual_code" | "lark_tenant_app";
type RunItem = {
  id: string;
  masked_identity: string;
  status: string;
  safe_error_code?: string | null;
  mailbox_id?: string | null;
};

const providerOptions: Array<{ id: Provider; title: string; note: string }> = [
  { id: "google_oauth", title: "Google OAuth", note: "Один ящик через Google" },
  { id: "google_workspace_dwd", title: "Google Workspace", note: "Все пользователи домена" },
  { id: "lark_individual_code", title: "Lark Mail", note: "Несколько кодов входа" },
  { id: "lark_tenant_app", title: "Lark tenant", note: "Все ящики организации" },
];

const errorLabel = (code?: string | null) => {
  if (code === "GOOGLE_DWD_NOT_CONFIGURED") return "DWD не настроен";
  if (code === "LARK_TENANT_APP_NOT_CONFIGURED") return "Tenant app не настроен";
  if (code === "LARK_CONNECTION_FAILED") return "Lark не принял код";
  if (code === "MAILBOX_ONBOARDING_AUTH_REQUIRED") return "Нужен вход";
  return code ? "Не подключён" : "";
};

export function MailboxOnboardingWizard({
  projectId,
  isOpen,
  onOpenChange,
  onConnected,
  onOpenWarmup,
}: {
  projectId: string;
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  onConnected: () => void;
  onOpenWarmup: () => void;
}) {
  const [provider, setProvider] = useState<Provider>("lark_individual_code");
  const [raw, setRaw] = useState("");
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState("");
  const [items, setItems] = useState<RunItem[]>([]);
  const [runId, setRunId] = useState("");
  const parsed = useMemo(() => {
    return raw
      .split(/\r?\n/)
      .map((line) => line.trim())
      .filter(Boolean)
      .map((line) => {
        const [email = "", ...credentialParts] = line.split(/[\t;,]+/);
        return { email: email.trim().toLowerCase(), credential: credentialParts.join(" ").trim() };
      });
  }, [raw]);

  const startGoogleOAuth = async () => {
    const csrf = await getCsrf();
    if (!csrf) throw new Error("AUTH_REQUIRED");
    const response = await fetch(`/api/projects/${projectId}/gmail/oauth/start`, {
      method: "POST",
      headers: { "content-type": "application/json", "x-csrf-token": csrf },
      body: "{}",
    });
    const result = (await response.json()) as { authorizationUrl?: string; error?: string };
    if (!response.ok || !result.authorizationUrl) throw new Error(result.error ?? "GOOGLE_OAUTH_FAILED");
    window.location.assign(result.authorizationUrl);
  };

  const checkTenantCapability = async () => {
    const csrf = await getCsrf();
    if (!csrf) throw new Error("AUTH_REQUIRED");
    const url = provider === "google_workspace_dwd"
      ? `/api/projects/${projectId}/google-workspace/users`
      : `/api/projects/${projectId}/lark/capability-check`;
    const response = await fetch(url, provider === "lark_tenant_app" ? {
      method: "POST",
      headers: { "content-type": "application/json", "x-csrf-token": csrf },
      body: "{}",
    } : undefined);
    const result = (await response.json()) as { error?: string; tenantApp?: boolean };
    if (!response.ok || result.tenantApp === false) throw new Error(result.error ?? "LARK_TENANT_APP_NOT_CONFIGURED");
    setMessage("Доступ подтверждён");
  };

  const connectBulk = async () => {
    if (!parsed.length) return;
    setBusy(true);
    setMessage("");
    try {
      const csrf = await getCsrf();
      if (!csrf) throw new Error("AUTH_REQUIRED");
      const created = await fetch(`/api/projects/${projectId}/mailbox-onboarding-runs`, {
        method: "POST",
        headers: { "content-type": "application/json", "x-csrf-token": csrf },
        body: JSON.stringify({
          provider,
          mode: "bulk",
          idempotencyKey: crypto.randomUUID(),
          items: parsed,
        }),
      });
      const createdData = (await created.json()) as { run?: { id: string }; error?: string };
      if (!created.ok || !createdData.run) throw new Error(createdData.error ?? "ONBOARDING_CREATE_FAILED");
      setRunId(createdData.run.id);
      setRaw("");
      const connected = await fetch(`/api/projects/${projectId}/mailbox-onboarding-runs/${createdData.run.id}/connect`, {
        method: "POST",
        headers: { "content-type": "application/json", "x-csrf-token": csrf },
        body: "{}",
      });
      if (!connected.ok) {
        const failure = (await connected.json()) as { error?: string };
        throw new Error(failure.error ?? "ONBOARDING_CONNECT_FAILED");
      }
      const state = await fetch(`/api/projects/${projectId}/mailbox-onboarding-runs/${createdData.run.id}`);
      const stateData = (await state.json()) as { items?: RunItem[] };
      setItems(stateData.items ?? []);
      onConnected();
    } catch (error) {
      const code = error instanceof Error ? error.message : "ONBOARDING_FAILED";
      setMessage(errorLabel(code) || "Подключение не выполнено");
    } finally {
      setBusy(false);
    }
  };

  const connectedCount = items.filter((item) => item.status === "connected").length;
  return (
    <ModalOverlay className="ui-modal-overlay" isOpen={isOpen} onOpenChange={onOpenChange} isDismissable>
      <Modal className="mail-onboarding-modal">
        <Dialog className="mail-onboarding-dialog">
          <header>
            <div>
              <Heading slot="title">Добавить почту</Heading>
              <p>Подключение с проверкой каждого ящика.</p>
            </div>
            <Button className="icon-button" aria-label="Закрыть" onPress={() => onOpenChange(false)}><X size={18} /></Button>
          </header>

          <div className="mail-onboarding-providers" role="list" aria-label="Способ подключения">
            {providerOptions.map((option) => (
              <Button
                key={option.id}
                className={`mail-onboarding-provider${provider === option.id ? " selected" : ""}`}
                onPress={() => { setProvider(option.id); setMessage(""); setItems([]); }}
              >
                <Mail size={18} />
                <span><strong>{option.title}</strong><small>{option.note}</small></span>
                {provider === option.id && <Check size={17} />}
              </Button>
            ))}
          </div>

          {provider === "google_oauth" ? (
            <section className="mail-onboarding-step">
              <ShieldCheck size={22} />
              <div><strong>Без пароля</strong><p>Google откроет отдельное окно согласия.</p></div>
              <Button className="primary-button" onPress={() => void startGoogleOAuth()} isDisabled={busy}>Продолжить</Button>
            </section>
          ) : provider === "lark_individual_code" ? (
            <section className="mail-onboarding-step vertical">
              <label htmlFor="bulk-mailboxes">Ящики и коды</label>
              <TextArea
                id="bulk-mailboxes"
                className="mail-onboarding-textarea"
                value={raw}
                onChange={(event) => setRaw(event.currentTarget.value)}
                placeholder={'name@company.com\tкод входа\nname2@company.com\tкод входа'}
              />
              <div className="mail-onboarding-meta"><span>{parsed.length} ящиков</span><span>Коды очищаются после сохранения</span></div>
              <Button className="primary-button" onPress={() => void connectBulk()} isDisabled={busy || parsed.length === 0}>
                {busy ? "Подключаем…" : "Подключить"}
              </Button>
            </section>
          ) : (
            <section className="mail-onboarding-step">
              <ShieldCheck size={22} />
              <div>
                <strong>{provider === "google_workspace_dwd" ? "Проверить DWD" : "Проверить tenant app"}</strong>
                <p>Массовый доступ включится только после проверки прав администратора.</p>
              </div>
              <Button className="secondary-button" onPress={() => void checkTenantCapability()} isDisabled={busy}>Проверить</Button>
            </section>
          )}

          {message && <p className="mail-onboarding-message" role="status">{message}</p>}
          {items.length > 0 && (
            <section className="mail-onboarding-results" aria-live="polite">
              <header><strong>Результат</strong><span>{connectedCount} из {items.length}</span></header>
              {items.map((item) => (
                <div key={item.id}><span>{item.masked_identity}</span><strong className={item.status === "connected" ? "success" : "error"}>{item.status === "connected" ? "Подключён" : errorLabel(item.safe_error_code)}</strong></div>
              ))}
              {connectedCount >= 2 && (
                <Button className="secondary-button" onPress={() => { onOpenChange(false); onOpenWarmup(); }}>Настроить прогрев</Button>
              )}
            </section>
          )}
          {runId && <small className="mail-onboarding-run">ID: {runId.slice(0, 8)}</small>}
        </Dialog>
      </Modal>
    </ModalOverlay>
  );
}
